<template>
  <div class="retailer-search-type-options-fi">
    <div class="retailer-search-type-options-fi__container">
      <div
        v-for="element in clonedElements"
        :key="element.name"
        :class="{
          'retailer-search-type-options-fi__option': true,
          'retailer-search-type-options-fi__option--active': element.selected,
        }"
      >
        <checkbox-fi
          ref="input"
          v-model="element.selected"
          class="retailer-search-type-options-fi__checkbox checkbox-fi--label-normal checkbox-fi--no-margin"
          :disabled="element.disabled"
          :variant="element.disabled ? 'disabled' : null"
          :name="element.key || element.name"
          @update:model-value="(val) => checkBoxChanged(val, element)"
        >
          <!-- keep the label in an element: a translator replaces a bare text node and Vue then patches a detached node -->
          <span>{{ element.name }}</span> <span class="count">({{ element.count }})</span>
        </checkbox-fi>

        <template v-if="element.subElements">
          <div
            v-for="subElement in element.subElements"
            :key="subElement.name"
            :class="{
              'retailer-search-type-options-fi__suboption': true,
              'retailer-search-type-options-fi__suboption--active': subElement.selected,
            }"
          >
            <checkbox-fi
              ref="input"
              v-model="subElement.selected"
              class="retailer-search-type-options-fi__checkbox checkbox-fi--label-normal checkbox-fi--no-margin"
              :disabled="subElement.disabled"
              :variant="subElement.disabled ? 'disabled' : null"
              :name="subElement.key || subElement.name"
              @update:model-value="(val) => checkBoxChanged(val, element, subElement)"
            >
              <span>{{ subElement.name }}</span> <span class="count">({{ subElement.count }})</span>
            </checkbox-fi>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CheckboxFi } from 'atoms';
import { ref } from 'vue';

defineOptions({ name: 'RetailerSearchTypeOptionsFi' });

const props = withDefaults(
  defineProps<{
    elements?: any[] | null;
    partners?: any[] | null;
    retailers?: any[] | null;
  }>(),
  {
    elements: null,
    partners: null,
    retailers: null,
  },
);

const emit = defineEmits<{
  (e: 'select-changed', payload: any): void;
}>();

const clonedElements = ref<any[]>(JSON.parse(JSON.stringify(props.elements ?? [])));
const activePartners = ref<any[]>(props.partners ?? []);
const activeRetailers = ref<any[]>(props.retailers ?? []);

function checkBoxChanged(value: boolean, element: any, subElement?: any) {
  if (value && subElement && !element.selected) {
    clonedElements.value.find((el) => el === element).selected = true;
  } else if (!value && !subElement && element.subElements.length) {
    clonedElements.value.forEach((el) => {
      el.subElements.forEach((subEl: any) => {
        subEl.selected = false;
      });
    });
  }

  if (value) {
    if (subElement) {
      if (subElement.type === 'partner') {
        if (!activePartners.value.some((el) => el.name === subElement.name)) {
          activePartners.value.push(subElement);
        }

        if (element.type === 'retailer') {
          activeRetailers.value = activeRetailers.value.filter((el) => el.name !== element.name);
          activeRetailers.value.unshift(element);
        } else if (!activePartners.value.some((el) => el.name === element.name)) {
          activePartners.value.push(element);
        }
      } else if (!activeRetailers.value.some((el) => el.name === subElement.name)) {
        activeRetailers.value.push(subElement);
      }
    } else if (element.type === 'partner') {
      if (!activePartners.value.some((el) => el.name === element.name)) {
        activePartners.value.push(element);
      }
    } else if (!activeRetailers.value.some((el) => el.name === element.name)) {
      activeRetailers.value.push(element);
    }
  } else if (subElement) {
    if (subElement.type === 'partner') {
      activePartners.value = activePartners.value.filter((el) => el.name !== subElement.name);
    } else {
      activeRetailers.value = activeRetailers.value.filter((el) => el.name !== subElement.name);
    }
  } else {
    if (element.type === 'partner') {
      activePartners.value = activePartners.value.filter((el) => el.name !== element.name);
    } else {
      activeRetailers.value = activeRetailers.value.filter((el) => el.name !== element.name);
    }

    if (element.subElements?.length) {
      element.subElements.forEach((subEl: any) => {
        if (subEl.type === 'partner') {
          activePartners.value = activePartners.value.filter((el) => el.name !== subEl.name);
        } else {
          activeRetailers.value = activeRetailers.value.filter((el) => el.name !== subEl.name);
        }
      });
    }
  }

  emit('select-changed', {
    newElements: clonedElements.value,
    activePartners: activePartners.value,
    activeRetailers: activeRetailers.value,
  });
}
</script>

<style scoped src="./retailer-search-type-options-fi" />

<style src="./retailer-search-type-options-fi-unscoped" />
